import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wand2, Calendar } from "lucide-react";
import { trackEvent, trackOnce } from "../lib/analytics";

// Hero copy A/B — only the words differ.
const HERO_COPY_KEY = "platebuddies_home_hero_copy_variant";
const HERO_COPY_VARIANTS = {
  family: "Design a Buddy your whole family will love.",
  brand: "Design a Buddy for tonight's dinner.",
};

// Hero image rotation — anchor A is a real customer photo (highest-converting).
const HERO_IMAGES = [
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_plate-buddies-launch/artifacts/5v5jqhhi_Maestro%20Meatball%20-%20Sterling.jpg",
    alt: "A real kid gives two enthusiastic thumbs up over his Maestro Meatball dinner",
    variant: "A-real-customer",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_plate-buddies-launch/artifacts/g7zil030_plate-buddies-premium-hero.jpg",
    alt: "The Plate Buddies premium kit",
    variant: "B-branded-premium",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_plate-buddies-launch/artifacts/wsf1p77i_sally-salad-recipe-lifestyle.jpg",
    alt: "Sally Salad Plate Buddy served on a family plate",
    variant: "C-branded-recipe",
  },
];

/**
 * Homepage Hero — focused, conversion-first.
 *
 * One job: get the visitor to click "Create a Buddy" or "Try the Meal
 * Planner" in the first screen. Everything else (shipping, guarantee, kits,
 * price, "no account needed", etc.) belongs lower on the page, not fighting
 * for attention inside the hero.
 *
 * Visual language from the Plate Buddies brand guide:
 *   - Cream page dominates (not full purple).
 *   - Teal eyebrow, Night Ink headline, Outfit Bold.
 *   - Two flat solid circle accents (teal + orange) in opposite corners.
 */
export default function Hero() {
  const hero = useMemo(() => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)], []);

  const [copyVariant, setCopyVariant] = useState("family");
  useEffect(() => {
    let v = null;
    try {
      v = sessionStorage.getItem(HERO_COPY_KEY);
      if (v === "picky") v = "brand";
      if (!v || !HERO_COPY_VARIANTS[v]) {
        v = Math.random() < 0.5 ? "family" : "brand";
        sessionStorage.setItem(HERO_COPY_KEY, v);
      } else {
        sessionStorage.setItem(HERO_COPY_KEY, v);
      }
    } catch {
      v = "brand";
    }
    setCopyVariant(v);
    trackOnce("home_hero_copy_view", { variant: v });
  }, []);

  const headline = HERO_COPY_VARIANTS[copyVariant];

  return (
    <section data-testid="section-hero" className="relative bg-pb-cream overflow-hidden">
      {/* Flat brand-guide accent circles — crisp solids, no blurs. */}
      <div aria-hidden className="hidden md:block absolute top-20 right-16 h-20 w-20 rounded-full bg-pb-brand-teal" />
      <div aria-hidden className="hidden md:block absolute -bottom-6 left-10 h-16 w-16 rounded-full bg-pb-brand-orange" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* === LEFT: focused content. One headline, one subhead, two CTAs. === */}
        <div className="lg:col-span-6 animate-fade-up">
          <p
            data-testid="text-eyebrow"
            className="font-display font-black uppercase tracking-[0.2em] text-pb-brand-teal text-xs sm:text-sm"
          >
            Play. Explore. Connect.
          </p>

          <h1
            data-testid={`text-hero-heading-${copyVariant}`}
            className="font-display font-black tracking-tight text-pb-brand-ink mt-5 text-5xl sm:text-6xl md:text-7xl leading-[0.98]"
          >
            {headline}
          </h1>

          <p data-testid="text-hero-sub" className="mt-6 max-w-lg text-lg sm:text-xl text-pb-brand-ink/70 leading-relaxed">
            Your child dreams up a character. We turn it into a printable Buddy that sits under their plate.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              data-testid="btn-hero-generate"
              to="/create"
              onClick={() => trackEvent("home_hero_cta_click", { variant: copyVariant, cta: "design_buddy" })}
              className="group rounded-full bg-pb-brand-orange text-white font-display font-black px-7 py-4 hover:scale-[1.03] transition inline-flex items-center justify-center gap-2 text-base"
            >
              <Wand2 className="h-5 w-5" />
              Create a Buddy
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              data-testid="btn-hero-planner"
              to="/planner"
              onClick={() => trackEvent("home_hero_cta_click", { variant: copyVariant, cta: "meal_planner" })}
              className="group rounded-full bg-white text-pb-brand-ink font-display font-black px-7 py-4 border-2 border-pb-brand-ink/10 hover:border-pb-brand-teal hover:text-pb-brand-teal transition text-base inline-flex items-center justify-center gap-2"
            >
              <Calendar className="h-5 w-5" />
              Try the Meal Planner
            </Link>
          </div>

          <p className="mt-6 text-sm text-pb-brand-ink/55 font-medium">
            Free to try. 30 seconds. No account needed.
          </p>
        </div>

        {/* === RIGHT: hero image, clean white frame === */}
        <div className="lg:col-span-6 relative">
          <div
            data-testid={`hero-image-variant-${hero.variant}`}
            className="relative rounded-[2rem] sm:rounded-[2.25rem] overflow-hidden border-[6px] border-white"
          >
            <img
              alt={hero.alt}
              src={hero.src}
              className="w-full h-[380px] sm:h-[520px] object-cover block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
